import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useWorkspace, invalidateWorkspaceCache } from "@/hooks/useWorkspace";
import { isCircuitOpen, trackQueryResult } from "@/lib/circuit-breaker";

import {
  normalizeInboxTabOrder,
  resolveConversationsVisibility,
  showAllAttentionTab,
  type ConversationAttentionFilter,
  type ConversationsVisibility,
} from "@/lib/conversation-filters";
import {
  canSeeAllAppointmentsInCalendars,
  resolveAppointmentsCalendarVisibility,
  usesAssignedOnlyCalendars,
  type AppointmentsCalendarVisibility,
} from "@/lib/appointment-filters";
import {
  canSeeAllDeals,
  resolveDealsVisibility,
  usesAssignedOnlyDeals,
  type DealsVisibility,
} from "@/lib/deal-filters";

export type { ConversationsVisibility, AppointmentsCalendarVisibility, DealsVisibility };

export type MemberProfileKind = "gestor" | "comercial" | "suporte" | "custom";

export interface MemberPermissions {
  conversations: boolean;
  crm: boolean;
  agents: boolean;
  products: boolean;
  integrations: boolean;
  settings: boolean;
  appointments: boolean;
  dashboard: boolean;
  automations: boolean;
  tasks: boolean;
  /** Acesso à página de Negociações (independente do CRM). */
  deals?: boolean;
  /** Pool do workspace + minhas, ou somente conversas atribuídas ao membro. */
  conversations_visibility: ConversationsVisibility;
  /** Todas as negociações dos funis permitidos ou só leads atribuídos ao membro. */
  deals_visibility?: DealsVisibility;
  /** Todas as agendas atribuídas ou só agendamentos de leads atribuídos ao membro. */
  appointments_calendar_visibility?: AppointmentsCalendarVisibility;
  /** Permite criar/editar/deletar funis de vendas (membros não-admin). */
  crm_manage_funnels?: boolean;
  /** Perfil pré-definido aplicado ao membro (gestor/comercial/suporte) ou 'custom'. */
  member_profile?: MemberProfileKind;
}


const DEFAULT_PERMISSIONS: MemberPermissions = {
  conversations: true,
  crm: true,
  agents: true,
  products: true,
  integrations: true,
  settings: true,
  appointments: true,
  dashboard: true,
  automations: true,
  tasks: true,
  deals: true,
  conversations_visibility: "workspace_inbox",
  appointments_calendar_visibility: "all",
  deals_visibility: "all",
};

function normalizePermissions(perms: Record<string, unknown> | null): MemberPermissions {
  if (!perms) return DEFAULT_PERMISSIONS;
  const visibilityRaw = perms.conversations_visibility;
  const conversations_visibility =
    visibilityRaw === "assigned_only" ||
    visibilityRaw === "workspace_inbox" ||
    visibilityRaw === "workspace_inbox_all"
      ? visibilityRaw
      : "workspace_inbox";
  const apptVisRaw = perms.appointments_calendar_visibility;
  const appointments_calendar_visibility: AppointmentsCalendarVisibility =
    apptVisRaw === "assigned_only" || apptVisRaw === "all" ? apptVisRaw : "all";
  const dealsVisRaw = perms.deals_visibility;
  const deals_visibility: DealsVisibility =
    dealsVisRaw === "assigned_only" || dealsVisRaw === "all" ? dealsVisRaw : "all";
  const profileRaw = perms.member_profile;
  const member_profile: MemberProfileKind | undefined =
    profileRaw === "gestor" || profileRaw === "comercial" || profileRaw === "suporte" || profileRaw === "custom"
      ? profileRaw
      : undefined;

  return {
    conversations: (perms.conversations as boolean | undefined) ?? true,
    crm: (perms.crm as boolean | undefined) ?? true,
    agents: (perms.agents as boolean | undefined) ?? false,
    products: (perms.products as boolean | undefined) ?? false,
    integrations: (perms.integrations as boolean | undefined) ?? false,
    settings: (perms.settings as boolean | undefined) ?? false,
    appointments: (perms.appointments as boolean | undefined) ?? true,
    dashboard: (perms.dashboard as boolean | undefined) ?? true,
    automations: (perms.automations as boolean | undefined) ?? false,
    tasks: (perms.tasks as boolean | undefined) ?? true,
    deals: (perms.deals as boolean | undefined) ?? (perms.crm as boolean | undefined) ?? false,
    conversations_visibility,
    appointments_calendar_visibility,
    deals_visibility,
    crm_manage_funnels: (perms.crm_manage_funnels as boolean | undefined) ?? false,
    member_profile,
  };
}


export const useMemberPermissions = () => {
  const { user } = useAuth();
  const {
    workspaceId,
    loading: workspaceLoading,
    memberId: storeMemberId,
    memberRole: storeRole,
    memberDisplayName: storeDisplayName,
    memberPermissions: storePermissions,
  } = useWorkspace(user?.id);
  const [permissions, setPermissions] = useState<MemberPermissions | null>(null);
  const [memberId, setMemberId] = useState<string | null>(null);
  const [memberDisplayName, setMemberDisplayName] = useState<string | null>(null);
  const [role, setRole] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  /** Fallback à workspace_members só uma vez por workspace (evita reconsultas repetidas). */
  const fallbackDoneForWorkspaceRef = useRef<string | null>(null);

  useEffect(() => {
    if (!user?.id) {
      setPermissions(null);
      setLoading(false);
      return;
    }

    if (workspaceLoading) {
      setLoading(true);
      return;
    }

    if (!workspaceId) {
      fallbackDoneForWorkspaceRef.current = null;
      setPermissions(DEFAULT_PERMISSIONS);
      setRole("owner");
      setMemberId(null);
      setMemberDisplayName(null);
      setLoading(false);
      return;
    }

    // Fonte única: reutilizar store quando tiver dados de membro
    if (storeMemberId != null || storeRole != null) {
      setMemberId(storeMemberId);
      setMemberDisplayName(storeDisplayName);
      setRole(storeRole);
      if (storeRole === "owner" || storeRole === "admin") {
        setPermissions(DEFAULT_PERMISSIONS);
      } else {
        setPermissions(normalizePermissions(storePermissions));
      }
      setLoading(false);
      return;
    }

    // Fallback só uma vez por workspace (recuperação/race); evita reconsultas em toda tela
    if (fallbackDoneForWorkspaceRef.current === workspaceId) {
      setLoading(false);
      return;
    }
    fallbackDoneForWorkspaceRef.current = workspaceId;

    const fetchMemberData = async () => {
      if (isCircuitOpen()) {
        setPermissions(null);
        setLoading(false);
        return;
      }

      try {
        const { data: member, error } = await supabase
          .from("workspace_members")
          .select("id, role, permissions, display_name")
          .eq("workspace_id", workspaceId)
          .eq("user_id", user.id)
          .single();

        trackQueryResult(error);

        if (error) {
          setPermissions(null);
          setLoading(false);
          return;
        }

        setMemberId(member?.id || null);
        setMemberDisplayName(member?.display_name || null);
        setRole(member?.role || null);

        if (member?.role === "owner" || member?.role === "admin") {
          setPermissions(DEFAULT_PERMISSIONS);
        } else {
          const storedPermissions = member?.permissions as unknown as Record<string, unknown> | null;
          setPermissions(storedPermissions ? normalizePermissions(storedPermissions) : DEFAULT_PERMISSIONS);
        }
      } catch {
        setPermissions(null);
      } finally {
        setLoading(false);
      }
    };

    fetchMemberData();
  }, [user?.id, workspaceId, workspaceLoading, storeMemberId, storeRole, storeDisplayName, storePermissions]);

  // Atualizar last_active_at apenas 1x por sessão (reduz writes no banco)
  useEffect(() => {
    if (!memberId) return;

    const SESSION_KEY = `fabchat_last_active_${memberId}`;
    const lastUpdate = sessionStorage.getItem(SESSION_KEY);
    const now = Date.now();

    // Só atualizar se nunca atualizou nesta sessão ou faz mais de 30 min
    if (!lastUpdate || now - parseInt(lastUpdate) > 30 * 60 * 1000) {
      supabase
        .from("workspace_members")
        .update({ last_active_at: new Date().toISOString() })
        .eq("id", memberId)
        .then(() => {
          sessionStorage.setItem(SESSION_KEY, String(now));
        });
    }
  }, [memberId]);

  const isOwner = role === "owner";
  const isAdmin = role === "admin" || role === "owner";
  const isMember = role === "member";

  // Helpers para verificar permissões específicas
  const canAccessConversations = isAdmin || permissions?.conversations === true;
  const canAccessCRM = isAdmin || permissions?.crm === true;
  const canAccessAgents = isAdmin || permissions?.agents === true;
  const canAccessProducts = isAdmin || permissions?.products === true;
  const canAccessIntegrations = isAdmin || permissions?.integrations === true;
  const canAccessSettings = isAdmin || permissions?.settings === true;
  const canAccessAppointments = isAdmin || permissions?.appointments === true;
  const canAccessDashboard = isAdmin || permissions?.dashboard === true;
  const canAccessBilling = isOwner; // Apenas owner pode ver billing
  const canAccessAutomations = isAdmin || permissions?.automations === true;
  const canAccessTasks = isAdmin || permissions?.tasks === true;
  const canAccessDeals =
    isAdmin || permissions?.deals === true || permissions?.crm === true;
  const canManageFunnels =
    isAdmin ||
    permissions?.crm_manage_funnels === true ||
    permissions?.member_profile === "gestor";

  /** Admin ou perfil gestor podem ver aba Desempenho e métricas da equipe. */
  const canViewTaskPerformance =
    isAdmin || permissions?.member_profile === "gestor";

  /** Admin ou perfil gestor enxergam todas as agendas (Fase 4 multi-agenda). */
  const canSeeAllCalendars =
    isAdmin || permissions?.member_profile === "gestor";

  const conversationsVisibility = resolveConversationsVisibility(
    permissions?.conversations_visibility,
    isAdmin
  );

  const appointmentsCalendarVisibility = resolveAppointmentsCalendarVisibility(
    permissions?.appointments_calendar_visibility,
    isAdmin,
  );

  const usesAssignedOnlyCalendarsVisibility = usesAssignedOnlyCalendars(
    appointmentsCalendarVisibility,
    isAdmin,
  );

  /** Admin ou visibilidade "all" enxergam todos os agendamentos nas agendas permitidas. */
  const canSeeAllAppointments = canSeeAllAppointmentsInCalendars(
    appointmentsCalendarVisibility,
    isAdmin,
  );

  const dealsVisibility = resolveDealsVisibility(
    permissions?.deals_visibility,
    isAdmin,
  );

  const usesAssignedOnlyDealsVisibility = usesAssignedOnlyDeals(dealsVisibility, isAdmin);

  const canSeeAllDealsInWorkspace = canSeeAllDeals(dealsVisibility, isAdmin);

  const inboxTabOrder = useMemo(() => {
    const raw =
      (storePermissions as Record<string, unknown> | null)?.conversations_inbox_tab_order ??
      null;
    return normalizeInboxTabOrder(raw, showAllAttentionTab(conversationsVisibility, isAdmin));
  }, [storePermissions, conversationsVisibility, isAdmin]);

  const saveInboxTabOrder = useCallback(
    async (order: ConversationAttentionFilter[]): Promise<boolean> => {
      if (!workspaceId) return false;
      const { error } = await supabase.rpc("update_my_conversations_inbox_tab_order", {
        _workspace_id: workspaceId,
        p_tab_order: order,
      });
      if (error) {
        console.error("Erro ao salvar ordem das abas:", error);
        return false;
      }
      invalidateWorkspaceCache();
      return true;
    },
    [workspaceId]
  );

  return {
    permissions,
    conversationsVisibility,
    appointmentsCalendarVisibility,
    usesAssignedOnlyCalendars: usesAssignedOnlyCalendarsVisibility,
    canSeeAllAppointments,
    inboxTabOrder,
    saveInboxTabOrder,
    memberId,
    memberDisplayName,
    role,
    loading,
    isOwner,
    isAdmin,
    isMember,
    canAccessConversations,
    canAccessCRM,
    canAccessAgents,
    canAccessProducts,
    canAccessIntegrations,
    canAccessSettings,
    canAccessAppointments,
    canAccessDashboard,
    canAccessBilling,
    canAccessAutomations,
    canAccessTasks,
    canAccessDeals,
    dealsVisibility,
    usesAssignedOnlyDeals: usesAssignedOnlyDealsVisibility,
    canSeeAllDealsInWorkspace,
    canManageFunnels,
    canViewTaskPerformance,
    canSeeAllCalendars,
  };
};
